import { Navigate, Outlet, useLocation, useParams } from "react-router-dom"

import useBatteryProposal, {
  isBatteryProposalAccessFailure,
} from "~/api/hooks/useBatteryProposal"

import {
  isBatteryProposalReadOnly,
  isBatteryReadOnlyPath,
} from "./battery-proposal-read-only"
import { BatteryOnlyProposalView } from "./BatteryOnlyProposalView"
import { isBatteryInitialSetupComplete } from "./setup/battery-initial-setup-progress"
import { BatteryInitialSetup } from "./setup/BatteryInitialSetup"
import { BatterySetupMenu } from "./setup/BatterySetupMenu"
import { batteryProposalPath } from "./setup/setup-steps"
import { PINNED_BATTERY_PROPOSAL_THEME } from "./theme/battery-proposal-theme"

export const BatteryOnlyProposalShell = (): JSX.Element => {
  const { proposalId } = useParams<{ proposalId: string }>()
  const { hash, pathname, search } = useLocation()
  const query = useBatteryProposal(proposalId)

  if (!query.data) {
    return (
      <div
        data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
        className="bg-bp-page text-bp-text flex min-h-screen items-center justify-center"
      >
        <p role="status">
          {query.isError
            ? isBatteryProposalAccessFailure(query.error)
              ? `Redirecting`
              : `Unable to load Battery-Only Proposal`
            : `Loading Battery-Only Proposal`}
        </p>
      </div>
    )
  }

  // Read-only comes from the proposal read contract, so the client's answer is
  // the server's: an actor whose saves would 403 never meets a setup wall or an
  // editing entry point. Every Battery proposal path renders through this
  // shell, which makes it the one gate — an editor of an incomplete proposal
  // meets required setup whichever link they followed, and a view-only actor
  // gets the ordinary page whichever link they followed.
  const readOnly = isBatteryProposalReadOnly(query.data)
  const setupRequired = !readOnly && !isBatteryInitialSetupComplete(query.data)
  // Built from the route parameter the proposal query is keyed on, so the
  // comparison and the redirect target cannot drift apart.
  const entryPath = batteryProposalPath(proposalId ?? query.data.id)
  const offLimits =
    setupRequired || (readOnly && !isBatteryReadOnlyPath(pathname))

  if (offLimits && pathname !== entryPath) {
    // Keep the query string and hash: a deep link's parameters are not the
    // seller's fault and are still meaningful once setup is done.
    return <Navigate to={`${entryPath}${search}${hash}`} replace />
  }

  return (
    <main
      data-testid="battery-only-proposal-shell"
      data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
      className="bg-bp-page text-bp-text min-h-screen"
    >
      <div className="mx-auto max-w-[var(--max-width-desktop)]">
        {setupRequired ? (
          <BatteryInitialSetup proposal={query.data} />
        ) : (
          <>
            {/* Keyed: another Proposal starts its own Updates panel and runs. */}
            <BatteryOnlyProposalView key={query.data.id} proposal={query.data} />
            <Outlet />
          </>
        )}
      </div>
      {readOnly || setupRequired ? null : <BatterySetupMenu />}
    </main>
  )
}
